import { Padel24Logo } from '../ui';
import type { Scope } from '../../lib/auth';
import { roleNameKey, useT } from '../../i18n';

export interface ScopeSelectorProps {
  /** Everywhere this account may act. */
  scopes: Scope[];
  onSelect: (tenantId: string) => void;
  onSignOut: () => void;
}

/**
 * Choose which organization to work in (#186).
 *
 * Shown only when there is an actual choice — one scope is selected without
 * asking, because a dialog whose only button is the one you must press is not a
 * decision, it is a delay.
 *
 * ORGANIZATION-NEUTRAL wording on purpose. The product says «klubb» today and
 * the backend stores a `Tenant`, but centre-versus-club is undecided (#302);
 * «organisasjon» stays true either way, and the day a centre contains clubs
 * this screen gains a level instead of lying about one.
 */
/** «Senter» / «Klubb» for the kind the server reported; '' for anything else. */
function kindLabel(kind: string, t: ReturnType<typeof useT>): string {
  if (kind === 'CENTRE') return t('auth.scope.kind.centre');
  if (kind === 'CLUB') return t('auth.scope.kind.club');
  return '';
}

export function ScopeSelector({ scopes, onSelect, onSignOut }: ScopeSelectorProps) {
  const t = useT();
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: '#6b21a8' }}
    >
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Padel24Logo size="xl" variant="full" tone="light" />
          </div>
          {/* ON THE PURPLE, NOT ON A CARD. The ink scale is chosen for
              `--paper`; `text-ink-3` on this `#6b21a8` is 2.78:1, under the
              4.5:1 floor (#781). 80% white is 6.11:1. Found across six screens
              at once when the contrast guard stopped being a hand-written list
              of two (#752). */}
          <p className="text-sm text-white/80">{t('app.tagline')}</p>
        </div>

        <div className="card p-6">
          <h2 className="font-display text-lg font-semibold mb-1">{t('auth.scope.selectOrg')}</h2>
          <p className="text-xs text-ink-3 mb-4">{t('auth.scope.leadShort')}</p>

          <div className="space-y-2">
            {scopes.map((s) => (
              <button
                key={s.tenantId}
                type="button"
                onClick={() => onSelect(s.tenantId)}
                className="w-full text-left rounded-lg border border-line p-3 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors"
              >
                <div className="font-display text-sm font-semibold text-ink-1">{s.name}</div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-ink-3 mt-0.5">
                  {/* Roles named for a person («Eier»), not by key; a custom
                      role keeps its key, which is still a name somebody chose. */}
                  {[
                    kindLabel(s.kind, t),
                    ...s.roles.map((r) => {
                      const key = roleNameKey(r);
                      return key ? t(key) : r;
                    }),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </button>
            ))}
          </div>

          <button type="button" onClick={onSignOut} className="btn-ghost w-full mt-4 text-xs">
            {t('app.signOut')}
          </button>
        </div>

        <p className="text-center text-[11px] text-white/70 mt-6">
          {t('app.footer', { brand: 'Starpoint' })}
        </p>
      </div>
    </div>
  );
}
